// src/tabs/ComptesPanel.jsx
// Gestion admin des comptes ménage — UI complète au-dessus de /api/admin-users.
// Affiché dans paramTab==="comptes" du ParamsPanel (admin only par construction
// puisque l'onglet Params n'est pas exposé au ménage).
//
// Sécurité : toutes les actions passent par /api/admin-users avec un Bearer
// JWT user (récupéré via supabase.auth.getSession()). L'endpoint serveur valide
// ce token et exige role=admin avant d'utiliser le service_role.
//
// DA (CHANTIER Paramètres) : habillage SEUL — table en .fbz-table-wrap rhabillée,
// UserModal Niveau 1 (modal-sheet intact), pastilles de rôle sémantiques, icônes
// lucide. Zéro logique/data : provisioning de rôles (admin-users) intact.

import { useState, useEffect, useMemo } from "react"
import { Users, UserPlus, Pencil, Trash2, AlertTriangle, X, Home, Sparkles, Compass } from "lucide-react"
import { DS } from "../lib/ui"
import { supabase } from "../supabase"
import { propDisplayName } from "../lib/format"
import { PrimaryButton, SecondaryButton, StatusPill, FieldLabel, daInputStyle } from "./_paramsUi"

// Meta de rôle (label + icône + ton de pastille sémantique). Repère d'identité,
// pas un signal d'état — tons doux distincts.
const ROLE_META = {
  menage:        { label: "Ménage",        icon: Sparkles, tone: "info" },
  orchestrateur: { label: "Orchestrateur", icon: Compass,  tone: "neutral" },
  proprietaire:  { label: "Propriétaire",  icon: Home,     tone: "success" },
}
function RolePill({ role }) {
  const m = ROLE_META[role] || ROLE_META.menage
  const Ico = m.icon
  return (
    <StatusPill tone={m.tone}>
      <Ico size={11} strokeWidth={1.9} /> {m.label}
    </StatusPill>
  )
}

export default function ComptesPanel({ cfg }) {
  const [users, setUsers]     = useState([])     // [{ id, email, logIds, createdAt, lastSignInAt }]
  const [loading, setLoading] = useState(true)
  const [err, setErr]         = useState(null)
  const [busy, setBusy]       = useState(false)  // mutation en cours → disable boutons
  const [editModal, setEditModal]   = useState(null) // null | { mode:'create'|'edit', user? }
  const [confirmDel, setConfirmDel] = useState(null) // null | user

  // Logements feuilles + chambres sous-loc, regroupés par propriétaire pour
  // l'UI checkboxes. Les chambres sont des ids à part entière dans logIds.
  const logsByProp = useMemo(() => {
    return (cfg.props || []).filter(Boolean).map(p => ({
      id: p.id,
      nom: p.nom,
      logs: (p.logs || []).filter(Boolean).flatMap(l => {
        const base = { id: l.id, nom: l.nom, sousLoc: false }
        const chambres = (l.chambres || []).filter(Boolean).map(c => ({
          id: c.id, nom: `${l.nom} → ${c.nom}`, sousLoc: true,
        }))
        return [base, ...chambres]
      }),
    }))
  }, [cfg])

  // Index id → nom pour affichage des chips de la liste.
  const logLabels = useMemo(() => {
    const m = {}
    logsByProp.forEach(p => p.logs.forEach(l => { m[l.id] = l.nom }))
    return m
  }, [logsByProp])

  // ── Fetcher avec Bearer token ──
  const callApi = async (method, body) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) throw new Error('Session expirée — recharge la page.')
    const init = {
      method,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
    if (body !== undefined) init.body = JSON.stringify(body)
    const res = await fetch('/api/admin-users', init)
    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json?.error || `Erreur HTTP ${res.status}`)
    return json
  }

  // ── Lifecycle ──
  const reload = async () => {
    setLoading(true); setErr(null)
    try {
      const { users } = await callApi('GET')
      setUsers(users || [])
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload() }, [])

  // ── Handlers ──
  const handleSave = async (form) => {
    setBusy(true)
    try {
      // Permissions 42 Phase 3 Lot 0 — on transmet TOUJOURS role + managed_agents
      // (array, vide hors orchestrateur). Le serveur ignore le pool si role≠
      // orchestrateur et le vide en base (M2).
      if (editModal.mode === 'create') {
        await callApi('POST', { email: form.email, password: form.password, logIds: form.logIds, role: form.role, managed_agents: form.managed_agents, first_name: form.first_name, last_name: form.last_name })
      } else {
        const body = { uid: editModal.user.id, email: form.email, logIds: form.logIds, role: form.role, managed_agents: form.managed_agents, first_name: form.first_name, last_name: form.last_name }
        if (form.password) body.password = form.password
        await callApi('PATCH', body)
      }
      setEditModal(null)
      await reload()
    } catch (e) {
      alert(e.message)
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (uid) => {
    setBusy(true)
    try {
      await callApi('DELETE', { uid })
      setConfirmDel(null)
      await reload()
    } catch (e) {
      alert(e.message)
    } finally {
      setBusy(false)
    }
  }

  // ── Render ──
  return (
    <div style={{fontFamily:DS.fontBody}}>
      <div className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:18,border:`0.5px solid ${DS.border}`,marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:DS.fontDisplay,fontSize:16,fontWeight:600,color:DS.marineDark}}>
            <Users size={18} strokeWidth={1.9} style={{color:DS.marine}} /> Comptes et accès
          </div>
          <PrimaryButton disabled={busy || loading} onClick={() => setEditModal({ mode: 'create' })} style={{padding:"9px 14px",fontSize:12.5}}>
            <UserPlus size={15} strokeWidth={2} /> Nouveau compte
          </PrimaryButton>
        </div>

        {loading && <div style={{textAlign:"center",padding:20,color:DS.textMuted,fontSize:12}}>Chargement…</div>}

        {err && (
          <div style={{display:"flex",alignItems:"flex-start",gap:9,background:DS.dangerBg,color:DS.dangerText,padding:"10px 12px",borderRadius:8,borderLeft:`3px solid ${DS.danger}`,fontSize:12.5,fontWeight:600,marginBottom:10}}>
            <AlertTriangle size={15} strokeWidth={1.9} style={{flex:"0 0 auto",marginTop:1}} /> <span>{err}</span>
          </div>
        )}

        {!loading && !err && users.length === 0 && (
          <div style={{textAlign:"center",padding:"32px 20px",color:DS.textSecondary}}>
            <span style={{display:"inline-flex",width:48,height:48,borderRadius:13,background:DS.cielSoft,color:DS.marine,alignItems:"center",justifyContent:"center",marginBottom:10}}><Users size={24} strokeWidth={1.7} /></span>
            <div style={{fontSize:13}}>Aucun compte. Crée le premier via « Nouveau compte ».</div>
          </div>
        )}

        {!loading && !err && users.length > 0 && (
          <div className="fbz-table-wrap" style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:560}}>
              <thead>
                <tr style={{background:DS.pageBg}}>
                  <th style={cTh}>Email</th>
                  <th style={cTh}>Logements accessibles</th>
                  <th style={cTh}>Dernier login</th>
                  <th style={{...cTh,textAlign:"center"}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="fbz-rowhover" style={{borderBottom:`1px solid ${DS.border}`}}>
                    <td style={{padding:"10px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{color:DS.marine,fontWeight:600}}>{u.email}</span>
                        <RolePill role={u.role} />
                      </div>
                      {(u.first_name || u.last_name) && (
                        <div style={{marginTop:3,fontSize:11.5,color:DS.textSecondary,fontWeight:600}}>
                          {`${u.first_name || ''} ${u.last_name || ''}`.trim()}
                        </div>
                      )}
                    </td>
                    <td style={{padding:"10px"}}>
                      {u.logIds.length === 0
                        ? <StatusPill tone="danger"><AlertTriangle size={11} strokeWidth={2} /> Aucun</StatusPill>
                        : (
                          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                            {u.logIds.map(lid => (
                              <span key={lid} style={{padding:"2px 8px",background:DS.cielSoft,color:DS.marineDeep,borderRadius:6,fontSize:10,fontWeight:600}}>
                                {logLabels[lid] || lid}
                              </span>
                            ))}
                          </div>
                        )
                      }
                    </td>
                    <td style={{padding:"10px",color:DS.textSecondary,fontSize:11}}>
                      {u.lastSignInAt
                        ? (Number.isNaN(new Date(u.lastSignInAt).getTime())
                            ? "—"
                            : new Date(u.lastSignInAt).toLocaleString("fr-FR"))
                        : <span style={{color:DS.textMuted}}>Jamais</span>}
                    </td>
                    <td style={{padding:"10px",textAlign:"center",whiteSpace:"nowrap"}}>
                      <button onClick={() => setEditModal({ mode: 'edit', user: u })} disabled={busy} title="Modifier" aria-label="Modifier"
                        className={busy?undefined:"fbz-press"} style={{background:"none",border:"none",cursor:busy?"not-allowed":"pointer",marginRight:6,opacity:busy?0.4:1,color:DS.marine,display:"inline-flex",padding:5,verticalAlign:"middle"}}><Pencil size={16} strokeWidth={1.8} /></button>
                      <button onClick={() => setConfirmDel(u)} disabled={busy} title="Supprimer" aria-label="Supprimer"
                        className={busy?undefined:"fbz-press"} style={{background:"none",border:"none",cursor:busy?"not-allowed":"pointer",opacity:busy?0.4:1,color:DS.textMuted,display:"inline-flex",padding:5,verticalAlign:"middle"}}><Trash2 size={16} strokeWidth={1.8} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editModal && (
        <UserModal
          mode={editModal.mode}
          user={editModal.user}
          logsByProp={logsByProp}
          allUsers={users}
          busy={busy}
          onSave={handleSave}
          onClose={() => setEditModal(null)}
        />
      )}

      {confirmDel && (
        <ConfirmModal
          title="Supprimer ce compte ?"
          msg={`Le compte ${confirmDel.email} sera supprimé définitivement de Supabase Auth. Cette action est irréversible.`}
          busy={busy}
          onCancel={() => setConfirmDel(null)}
          onOk={() => handleDelete(confirmDel.id)}
        />
      )}
    </div>
  )
}

const cTh = {padding:"8px 10px",textAlign:"left",fontSize:10,color:DS.textSecondary,textTransform:"uppercase",letterSpacing:.5,fontWeight:700}

// ── Sous-composants ──

// Permissions 42 Phase 3 Lot 0 — 3 types provisionnables (admin jamais ici).
const ROLE_OPTIONS = [
  { id: 'menage',        label: 'Ménage',        icon: Sparkles },
  { id: 'orchestrateur', label: 'Orchestrateur', icon: Compass },
  { id: 'proprietaire',  label: 'Propriétaire',  icon: Home },
]

function UserModal({ mode, user, logsByProp, allUsers, busy, onSave, onClose }) {
  const isEdit = mode === 'edit'
  const [email, setEmail]       = useState(user?.email || '')
  const [password, setPassword] = useState('')
  // Identité d'affichage (Lot 0) — prénom/nom. Optionnels, alimentent l'accueil
  // « Bonjour [Prénom] » + l'avatar à initiale côté espace proprio/agent.
  const [firstName, setFirstName] = useState(user?.first_name || '')
  const [lastName, setLastName]   = useState(user?.last_name || '')
  const [logIds, setLogIds]     = useState(new Set(user?.logIds || []))
  // Type de compte (défaut ménage, rétro-compat). Le passage à 'orchestrateur'
  // révèle le champ « Pool d'agents ».
  const [role, setRole]         = useState(user?.role || 'menage')
  // Pool d'agents ménage pilotés (orchestrateur uniquement). Set d'emails.
  const [managedAgents, setManagedAgents] = useState(new Set(user?.managed_agents || []))
  const [err, setErr]           = useState(null)

  // Comptes ménage existants éligibles au pool (source = liste déjà chargée).
  // Affichés par email. Un orchestrateur ne pilote QUE des agents ménage.
  const menageAccounts = useMemo(
    () => (allUsers || []).filter(u => u.role === 'menage'),
    [allUsers]
  )

  const toggleAgent = (em) => setManagedAgents(s => {
    const next = new Set(s)
    if (next.has(em)) next.delete(em); else next.add(em)
    return next
  })

  const toggleLog = (lid) => setLogIds(s => {
    const next = new Set(s)
    if (next.has(lid)) next.delete(lid); else next.add(lid)
    return next
  })

  const toggleProp = (prop) => {
    const allIds = prop.logs.map(l => l.id)
    const allChecked = allIds.every(id => logIds.has(id))
    setLogIds(s => {
      const next = new Set(s)
      if (allChecked) allIds.forEach(id => next.delete(id))
      else allIds.forEach(id => next.add(id))
      return next
    })
  }

  // Validation pré-save : cohérente avec api/admin-users.js (PWD_MIN=6, ≥1 logement).
  const validate = () => {
    if (!email.trim()) return 'Email requis'
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return 'Email invalide'
    if (!isEdit && password.length < 6) return 'Mot de passe : 6 caractères minimum'
    if (isEdit && password.length > 0 && password.length < 6) return 'Mot de passe : 6 caractères minimum (laisser vide pour ne pas changer)'
    if (logIds.size === 0) return 'Au moins un logement doit être sélectionné'
    return null
  }

  const handleSubmit = () => {
    const v = validate()
    if (v) { setErr(v); return }
    setErr(null)
    // managed_agents transmis seulement s'il y a du sens (orchestrateur) ; sinon
    // array vide → le serveur le videra de toute façon (M2).
    onSave({
      email: email.trim(),
      password,
      logIds: [...logIds],
      role,
      managed_agents: role === 'orchestrateur' ? [...managedAgents] : [],
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    })
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(20,30,45,.55)",zIndex:1001,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div className="fbz-modal-sheet" style={{background:DS.card,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",fontFamily:DS.fontBody}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${DS.border}`,position:"sticky",top:0,background:DS.card,zIndex:2,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:DS.fontDisplay,fontWeight:600,color:DS.marineDark,fontSize:17}}>
            {isEdit ? <Pencil size={17} strokeWidth={1.9} /> : <UserPlus size={17} strokeWidth={1.9} />}
            {isEdit ? 'Modifier le compte' : 'Nouveau compte'}
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Fermer" style={{background:"none",border:"none",cursor:busy?"not-allowed":"pointer",color:DS.textMuted,display:"inline-flex"}}><X size={22} /></button>
        </div>

        <div style={{padding:"16px 20px"}}>
          <div style={{marginBottom:14}}>
            <FieldLabel>Type de compte</FieldLabel>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {ROLE_OPTIONS.map(opt => {
                const active = role === opt.id
                const Ico = opt.icon
                return (
                  <button key={opt.id} type="button" onClick={() => setRole(opt.id)} disabled={busy}
                    className={busy?undefined:"fbz-press"}
                    style={{flex:"1 1 100px",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5,padding:"9px 6px",borderRadius:DS.radiusControl,fontSize:12,fontWeight:600,fontFamily:DS.fontBody,cursor:busy?"not-allowed":"pointer",
                      border:active?`1px solid ${DS.corail}`:`0.5px solid ${DS.borderHover}`,background:active?DS.corail:DS.card,color:active?"#fff":DS.textSecondary}}>
                    <Ico size={14} strokeWidth={1.9} /> {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div style={{marginBottom:12}}>
            <FieldLabel>Email</FieldLabel>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="menage@exemple.com" style={daInputStyle} disabled={busy}/>
          </div>

          <div style={{marginBottom:12,display:"flex",gap:10,flexWrap:"wrap"}}>
            <div style={{flex:"1 1 140px"}}>
              <FieldLabel>
                Prénom
                <span style={{color:DS.textMuted,textTransform:"none",letterSpacing:0,fontWeight:500,marginLeft:6}}>— optionnel</span>
              </FieldLabel>
              <input type="text" value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder="Prénom" style={daInputStyle} disabled={busy} data-testid="compte-first-name"/>
            </div>
            <div style={{flex:"1 1 140px"}}>
              <FieldLabel>
                Nom
                <span style={{color:DS.textMuted,textTransform:"none",letterSpacing:0,fontWeight:500,marginLeft:6}}>— optionnel</span>
              </FieldLabel>
              <input type="text" value={lastName} onChange={e=>setLastName(e.target.value)} placeholder="Nom" style={daInputStyle} disabled={busy} data-testid="compte-last-name"/>
            </div>
          </div>

          <div style={{marginBottom:12}}>
            <FieldLabel>
              Mot de passe
              {isEdit && <span style={{color:DS.textMuted,textTransform:"none",letterSpacing:0,fontWeight:500,marginLeft:6}}>— laisser vide pour ne pas changer</span>}
            </FieldLabel>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              placeholder={isEdit ? '••••••' : '6 caractères minimum'} style={daInputStyle} disabled={busy}/>
          </div>

          <div style={{marginBottom:12}}>
            <FieldLabel>
              Logements accessibles
              <span style={{color:logIds.size===0?DS.danger:DS.textMuted,textTransform:"none",letterSpacing:0,fontWeight:600,marginLeft:6}}>
                ({logIds.size} cochés · min 1)
              </span>
            </FieldLabel>

            {logsByProp.map(prop => {
              const allChecked = prop.logs.every(l => logIds.has(l.id))
              return (
                <div key={prop.id} style={{background:DS.pageBg,borderRadius:8,padding:10,marginBottom:8,border:`0.5px solid ${DS.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:700,color:DS.marine}}><Home size={13} strokeWidth={1.9} /> {propDisplayName(prop)}</div>
                    <button type="button" onClick={()=>toggleProp(prop)} disabled={busy}
                      style={{background:"none",border:"none",cursor:busy?"not-allowed":"pointer",fontSize:10.5,color:DS.corail,fontWeight:600,fontFamily:DS.fontBody,padding:0}}>
                      {allChecked ? 'Tout décocher' : 'Tout cocher'}
                    </button>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {prop.logs.map(l => (
                      <label key={l.id} style={{display:"flex",alignItems:"center",gap:8,cursor:busy?"not-allowed":"pointer",fontSize:12,color:DS.textPrimary}}>
                        <input type="checkbox" checked={logIds.has(l.id)} onChange={()=>toggleLog(l.id)} disabled={busy}/>
                        <span>{l.nom}</span>
                        {l.sousLoc && <span style={{background:DS.marine,color:"#fff",fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:4}}>SOUS-LOC</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          {role === 'orchestrateur' && (
            <div style={{marginBottom:12}}>
              <FieldLabel>
                Pool d'agents pilotés
                <span style={{color:DS.textMuted,textTransform:"none",letterSpacing:0,fontWeight:600,marginLeft:6}}>
                  ({managedAgents.size} sélectionné{managedAgents.size > 1 ? 's' : ''})
                </span>
              </FieldLabel>
              {menageAccounts.length === 0 ? (
                <div style={{background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:8,padding:12,fontSize:12,color:DS.textSecondary}}>
                  Aucun compte ménage existant. Crée d'abord des agents ménage pour les rattacher à un orchestrateur.
                </div>
              ) : (
                <div style={{background:DS.pageBg,borderRadius:8,padding:10,border:`0.5px solid ${DS.border}`,display:"flex",flexDirection:"column",gap:4}}>
                  {menageAccounts.map(a => (
                    <label key={a.id} style={{display:"flex",alignItems:"center",gap:8,cursor:busy?"not-allowed":"pointer",fontSize:12,color:DS.textPrimary}}>
                      <input type="checkbox" checked={managedAgents.has(a.email)} onChange={()=>toggleAgent(a.email)} disabled={busy}/>
                      <span>{a.email}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {err && (
            <div style={{display:"flex",alignItems:"flex-start",gap:9,background:DS.dangerBg,color:DS.dangerText,padding:"10px 12px",borderRadius:8,borderLeft:`3px solid ${DS.danger}`,fontSize:12.5,fontWeight:600,marginBottom:10}}>
              <AlertTriangle size={15} strokeWidth={1.9} style={{flex:"0 0 auto",marginTop:1}} /> <span>{err}</span>
            </div>
          )}
        </div>

        <div style={{padding:"12px 20px",borderTop:`1px solid ${DS.border}`,position:"sticky",bottom:0,background:DS.card,display:"flex",gap:8}}>
          <SecondaryButton disabled={busy} onClick={onClose} style={{flex:1,padding:11}}>Annuler</SecondaryButton>
          <PrimaryButton disabled={busy} onClick={handleSubmit} style={{flex:2,padding:11}}>
            {busy ? '...' : isEdit ? 'Enregistrer' : 'Créer le compte'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}

function ConfirmModal({ title, msg, busy, onOk, onCancel }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(20,30,45,.55)",zIndex:1002,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:DS.card,borderRadius:16,padding:24,maxWidth:340,width:"100%",fontFamily:DS.fontBody}}>
        <div style={{fontFamily:DS.fontDisplay,fontWeight:600,color:DS.marineDark,fontSize:16,marginBottom:8}}>{title}</div>
        <div style={{fontSize:12.5,color:DS.textSecondary,marginBottom:16,lineHeight:1.5}}>{msg}</div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <SecondaryButton disabled={busy} onClick={onCancel} style={{padding:"8px 14px",fontSize:12}}>Annuler</SecondaryButton>
          <PrimaryButton disabled={busy} onClick={onOk} style={{padding:"8px 14px",fontSize:12,background: busy ? "#cdd5df" : DS.danger}}>
            {busy ? '...' : 'Supprimer'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}
